import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { apiService } from '../services/api';
import { Staff, StaffRole, StaffStatus } from '../types';
import { useToast } from '../context/ToastContext';

type StaffFormValues = {
  name: string;
  mobile: string;
  shopOwnerType: StaffRole;
  status: StaffStatus;
  wa_enabled: boolean;
};

const STAFF_ROLE_OPTIONS: Array<{ label: string; value: StaffRole }> = [
  { label: 'Owner', value: 'shopOwner' },
  { label: 'Manager', value: 'manager' },
  { label: 'Order Manager', value: 'orderManager' },
  { label: 'Service Staff', value: 'serviceStaff' },
  { label: 'Kitchen Staff', value: 'kitchenStaff' },
  { label: 'Delivery Staff', value: 'deliveryStaff' },
  { label: 'Counter Staff', value: 'counterStaff' },
];

const StaffList: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StaffStatus>('active');
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [waTogglingId, setWaTogglingId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StaffFormValues>({
    defaultValues: {
      name: '',
      mobile: '',
      shopOwnerType: 'shopOwner',
      status: 'active',
      wa_enabled: false,
    },
  });

  const roleLabelMap = useMemo(() => {
    return STAFF_ROLE_OPTIONS.reduce<Record<string, string>>((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    fetchStaffs(statusFilter);
  }, [statusFilter]);

  const fetchStaffs = async (status: StaffStatus) => {
    try {
      setLoading(true);
      const response = await apiService.getStaffs(status);
      if (response.status) {
        setStaffs((response.data || response.doc || []) as Staff[]);
      } else {
        showError('Failed to fetch staffs', response.message || 'Unable to load staff members.');
      }
    } catch (error) {
      showError('Error', 'An error occurred while loading staff members.');
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingStaff(null);
    reset({
      name: '',
      mobile: '',
      shopOwnerType: 'shopOwner',
      status: 'active',
      wa_enabled: false,
    });
  };

  const openCreateForm = () => {
    setEditingStaff(null);
    reset({
      name: '',
      mobile: '',
      shopOwnerType: 'shopOwner',
      status: 'active',
      wa_enabled: false,
    });
    setIsFormOpen(true);
  };

  const openEditForm = (staff: Staff) => {
    const staffId = staff._id || staff.id;
    if (!staffId) {
      showError('Missing ID', 'Unable to edit this staff member because ID is missing.');
      return;
    }

    setEditingStaff({ ...staff, id: staffId });
    reset({
      name: staff.name || '',
      mobile: staff.mobile || '',
      shopOwnerType: staff.shopOwnerType || 'shopOwner',
      status: staff.status || 'active',
      wa_enabled: Boolean(staff.wa_enabled),
    });
    setIsFormOpen(true);
  };

  const handleWaEnabledToggle = async (staff: Staff, enabled: boolean) => {
    const staffId = staff._id || staff.id;
    if (!staffId) {
      showError('Missing ID', 'Unable to update this staff member because ID is missing.');
      return;
    }

    setWaTogglingId(staffId);
    try {
      const response = await apiService.updateStaff({
        id: staffId,
        name: staff.name,
        mobile: staff.mobile,
        shopOwnerType: staff.shopOwnerType,
        status: staff.status || 'active',
        wa_enabled: enabled,
      });

      if (response.status) {
        setStaffs((prev) =>
          prev.map((s) => ((s._id || s.id) === staffId ? { ...s, wa_enabled: enabled } : s))
        );
        showSuccess('WhatsApp notifications updated');
      } else {
        showError('Update failed', response.message || 'Unable to update WhatsApp notifications.');
      }
    } catch {
      showError('Error', 'An error occurred while updating WhatsApp notifications.');
    } finally {
      setWaTogglingId(null);
    }
  };

  const onSubmit = async (values: StaffFormValues) => {
    setIsSubmitting(true);
    try {
      if (editingStaff?.id) {
        const response = await apiService.updateStaff({
          id: editingStaff.id,
          name: values.name.trim(),
          mobile: values.mobile.trim(),
          shopOwnerType: values.shopOwnerType,
          status: values.status,
          wa_enabled: values.wa_enabled,
        });

        if (!response.status) {
          showError('Failed to update staff', response.message || 'Unable to update staff.');
          return;
        }

        showSuccess('Staff updated successfully');
      } else {
        const response = await apiService.createStaff({
          name: values.name.trim(),
          mobile: values.mobile.trim(),
          shopOwnerType: values.shopOwnerType,
          wa_enabled: values.wa_enabled,
        });

        if (!response.status) {
          showError('Failed to create staff', response.message || 'Unable to create staff.');
          return;
        }

        showSuccess('Staff created successfully');
      }

      closeForm();
      await fetchStaffs(statusFilter);
    } catch (error) {
      showError('Error', 'An error occurred while saving staff details.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setStatusFilter('active')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              statusFilter === 'active' ? 'bg-orange-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('inactive')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              statusFilter === 'inactive' ? 'bg-orange-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Inactive
          </button>
        </div>

        <button
          onClick={openCreateForm}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Staff
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      ) : staffs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No {statusFilter} staff members found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staffs.map((staff) => {
            const staffId = staff._id || staff.id;
            return (
              <div key={staffId || `${staff.mobile}-${staff.name}`} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{staff.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{staff.mobile}</p>
                    <p className="text-sm text-gray-700 mt-2">{roleLabelMap[staff.shopOwnerType] || staff.shopOwnerType}</p>
                    <span className={`inline-flex mt-3 px-2.5 py-1 text-xs rounded-full ${
                      (staff.status || 'active') === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {(staff.status || 'active').toUpperCase()}
                    </span>
                  </div>
                  <button onClick={() => openEditForm(staff)} className="text-orange-600 hover:text-orange-800 flex-shrink-0">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">WhatsApp notifications</p>
                    <p className="text-xs text-gray-500 mt-0.5">Receive order notification on whatsapp</p>
                  </div>
                  <label
                    className={`relative inline-flex items-center flex-shrink-0 ${
                      waTogglingId !== staffId ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(staff.wa_enabled)}
                      onChange={(e) => handleWaEnabledToggle(staff, e.target.checked)}
                      disabled={waTogglingId === staffId}
                      className="sr-only peer"
                    />
                    <div
                      className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${
                        waTogglingId === staffId ? 'opacity-50' : ''
                      }`}
                    >
                      {waTogglingId === staffId && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto !mt-0">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={closeForm} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingStaff ? 'Edit Staff' : 'Add Staff'}
                </h3>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-500 text-2xl leading-none">
                  x
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter staff name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                  <input
                    {...register('mobile', {
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^[0-9]{10,15}$/,
                        message: 'Enter a valid mobile number',
                      },
                    })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter mobile number"
                  />
                  {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    {...register('shopOwnerType', { required: 'Role is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {STAFF_ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="min-w-0 pr-2">
                    <p className="text-sm font-medium text-gray-900">WhatsApp notifications</p>
                    <p className="text-xs text-gray-500 mt-0.5">Receive order notification on whatsapp</p>
                  </div>
                  <label className="relative inline-flex items-center flex-shrink-0 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('wa_enabled')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600" />
                  </label>
                </div>

                {editingStaff && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select
                      {...register('status', { required: 'Status is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? 'Saving...' : editingStaff ? 'Update Staff' : 'Create Staff'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffList;
